import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "../../../ui/common/button";
import { TableCell, TableRow } from "../../../ui/common/table";
import { TooltipProvider } from "../../../ui/common/tooltip";
import {
  VERSION_HISTORY_COLUMNS,
  VERSION_HISTORY_COPY
} from "../constants/versionHistory.constants";
import {
  VersionBadgeTone,
  VersionDetailsDraft,
  VersionHistoryGroup
} from "../types/versionHistory.types";
import { buildVersionDetailsSummaryParts } from "../utils/versionDetails.utils";
import { buildReportingYearOptions, resolveVersionLabel } from "../utils/versionHistory.utils";
import { VersionCalculationRecordsRows } from "./VersionCalculationRecordsTable";
import { VersionDetailsEditTrigger } from "./VersionDetailsEditTrigger";
import { VersionDetailsRow } from "./VersionDetailsRow";
import { VersionDetailsSummary } from "./VersionDetailsSummary";
import { VersionNameCell } from "./VersionNameCell";
import { VersionRowActions } from "./VersionRowActions";
import { VersionStatusBadge } from "./VersionStatusBadge";

interface VersionHistoryRowProps {
  version: VersionHistoryGroup;
  isExpanded: boolean;
  selectedRecordId: string | undefined;
  isEditingName: boolean;
  otherVersionNames: string[];
  isSavingName: boolean;
  currencyCode: string;
  isEditingDetails: boolean;
  isSavingDetails: boolean;
  isActivating: boolean;
  isDeleting: boolean;
  onToggle: () => void;
  onSelectRecord: (recordId: string) => void;
  onStartRename: () => void;
  onCommitRename: (value: string) => Promise<string | null>;
  onCancelRename: () => void;
  onStartEditDetails: () => void;
  onCommitDetails: (draft: VersionDetailsDraft) => Promise<string | null>;
  onCancelEditDetails: () => void;
  onRequestDeleteDetails: () => void;
  onGoToMapping: () => void;
  onRequestActivate: () => void;
  onRequestDelete: () => void;
}

const reportingYearOptions = buildReportingYearOptions();

/**
 * The version bar spans every column (matching the Lovable prototype: a version is a highlighted
 * bar, not a row of its own columns) via colSpan. When expanded, the Version details block and
 * then the calculation records render directly underneath as rows of this same table -- see
 * VersionDetailsRow and VersionCalculationRecordsRows.
 */
export const VersionHistoryRow = ({
  version,
  isExpanded,
  selectedRecordId,
  isEditingName,
  otherVersionNames,
  isSavingName,
  currencyCode,
  isEditingDetails,
  isSavingDetails,
  isActivating,
  isDeleting,
  onToggle,
  onSelectRecord,
  onStartRename,
  onCommitRename,
  onCancelRename,
  onStartEditDetails,
  onCommitDetails,
  onCancelEditDetails,
  onRequestDeleteDetails,
  onGoToMapping,
  onRequestActivate,
  onRequestDelete
}: VersionHistoryRowProps) => {
  const hasVersionId = !!version.versionId;
  // The legacy bucket is a synthetic grouping with no version document to write to.
  const canEditDetails = hasVersionId && !version.isLegacy;
  const versionLabel = resolveVersionLabel(version);
  const summaryParts = buildVersionDetailsSummaryParts(version, currencyCode);

  return (
    <>
      <TableRow className="tw-bg-primary/5 hover:tw-bg-primary/5">
        <TableCell colSpan={VERSION_HISTORY_COLUMNS.length} className="tw-py-2">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-flex-wrap">
            <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="tw-h-7 tw-w-7 tw-shrink-0"
                aria-expanded={isExpanded}
                aria-label={isExpanded ? "Collapse version" : "Expand version"}
                onClick={onToggle}
              >
                {isExpanded ? (
                  <ChevronDown className="tw-h-4 tw-w-4" />
                ) : (
                  <ChevronRight className="tw-h-4 tw-w-4" />
                )}
              </Button>
              <VersionNameCell
                versionNumber={version.versionNumber}
                versionName={version.versionName}
                canEdit={hasVersionId}
                isEditing={isEditingName}
                otherVersionNames={otherVersionNames}
                isSaving={isSavingName}
                onStartEdit={onStartRename}
                onCommit={onCommitRename}
                onCancel={onCancelRename}
              />
              <span className="tw-text-xs tw-font-mono tw-text-muted-foreground">
                {VERSION_HISTORY_COPY.calculationCountLabel(version.calculationCount)}
              </span>
              {version.isDefault ? (
                <VersionStatusBadge
                  tone={VersionBadgeTone.Active}
                  label={VERSION_HISTORY_COPY.activeBadge}
                />
              ) : null}
              <VersionStatusBadge
                tone={
                  version.isCalculated
                    ? VersionBadgeTone.Calculated
                    : VersionBadgeTone.NotCalculated
                }
                label={
                  version.isCalculated
                    ? VERSION_HISTORY_COPY.calculatedBadge
                    : VERSION_HISTORY_COPY.notCalculatedBadge
                }
              />
              {/* Expanded rows show the full details block below instead. */}
              {!isExpanded ? <VersionDetailsSummary parts={summaryParts} /> : null}
            </div>

            <TooltipProvider delayDuration={200}>
              <div className="tw-flex tw-items-center tw-gap-1">
                {canEditDetails ? (
                  <VersionDetailsEditTrigger
                    versionLabel={versionLabel}
                    disabled={isEditingDetails}
                    onClick={onStartEditDetails}
                  />
                ) : null}
                <VersionRowActions
                  canActivate={version.canActivate}
                  canGoToMapping={hasVersionId}
                  canDelete={hasVersionId}
                  isActivating={isActivating}
                  isDeleting={isDeleting}
                  onGoToMapping={onGoToMapping}
                  onRequestActivate={onRequestActivate}
                  onRequestDelete={onRequestDelete}
                />
              </div>
            </TooltipProvider>
          </div>
        </TableCell>
      </TableRow>

      {isExpanded ? (
        <>
          <VersionDetailsRow
            version={version}
            yearOptions={reportingYearOptions}
            currencyCode={currencyCode}
            versionLabel={versionLabel}
            isEditing={isEditingDetails}
            isSaving={isSavingDetails}
            onCommit={onCommitDetails}
            onCancel={onCancelEditDetails}
            onRequestDelete={onRequestDeleteDetails}
          />
          <VersionCalculationRecordsRows
            records={version.records}
            selectedRecordId={selectedRecordId}
            onSelectRecord={onSelectRecord}
          />
        </>
      ) : null}
    </>
  );
};
